/**
 * Solanaland
 *
 * Author: Phia Nasol
 * Company: Digiteer Inc.
 *
 * https://www.digiteer.digital/
 *
 */

import React from "react";
import { Col, Container, Modal, Row } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { api, ApiError } from "core/api";
import { Button, Input } from "core/components";
import { ChangePasswordFormData, ChangePasswordSchema } from "core/schema";
import { change_password_admin_user_path } from "routes";

type ChangePasswordFormProps = {
  allocation_rule: ChangePasswordFormData;
};

const ChangePasswordForm = ({ user, show, onCancel, onSubmit }: ChangePasswordFormProps): JSX.Element => {
  const {
    register,
    handleSubmit,
    reset,
    clearErrors,
    formState: { isSubmitting, isSubmitSuccessful, errors }
  } = useForm<ChangePasswordFormData>({
    mode: "onChange",
    defaultValues: ChangePasswordSchema.cast({ id: user.id }),
    resolver: yupResolver(ChangePasswordSchema)
  });
  const [formErrors, setFormErrors] = React.useState<Array<string>>([]);

  const submit = async (data: ChangePasswordFormData): Promise<unknown> => {
    try {
      await api.put(change_password_admin_user_path(user.id), data);
      onSubmit();
    } catch (error) {
      if (error instanceof ApiError) {
        setFormErrors(error.all());
      } else {
        setFormErrors([error?.message]);
      }
    }
  };

  React.useEffect(() => {
    if (isSubmitSuccessful) {
      reset({ id: user.id, password: null, confirm_password: null });
    }

    return () => {
      clearErrors();
    };
  }, [reset]);

  return (
    <React.StrictMode>
      <Modal dialogClassName="modal-dialog-centered" show={show}>
        <Modal.Header>{`Change password: ${user?.last_name}`}</Modal.Header>
        <Modal.Body>
          <Container>
            <Row>
              <Col>
                <Input
                  {...register("password")}
                  label="New Password"
                  type="password"
                  required
                  errorMessage={errors.password?.message}
                  maxLength={20}
                />
                <Input
                  {...register("confirm_password")}
                  label="Confirm Password"
                  required
                  type="password"
                  errorMessage={errors.confirm_password?.message}
                  maxLength={20}
                />
              </Col>
            </Row>
          </Container>
        </Modal.Body>
        <Modal.Footer>
          <Button
            onClick={() => {
              clearErrors();
              onCancel();
            }}
            icon="times"
            title="Cancel"
            type="reset"
          />
          <Button
            className="button--brand"
            icon="save"
            title="Yes, change password."
            disabled={isSubmitting}
            onClick={handleSubmit(async (data) => await submit(data))}
          />
        </Modal.Footer>
      </Modal>
    </React.StrictMode>
  );
};

export default ChangePasswordForm;
